import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import {
  RefreshCw,
  Calendar,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Clock,
  DollarSign,
  Target,
  Rocket,
  Brain,
  BarChart3,
  Zap,
  ChevronRight,
  CheckCircle,
  XCircle,
  Sparkles,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  ShieldAlert,
  Star
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

export default function Earnings() {
  const [earnings, setEarnings] = useState(null);
  const [earningsPlays, setEarningsPlays] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchSymbol, setSearchSymbol] = useState("");
  const [prediction, setPrediction] = useState(null);
  const [predicting, setPredicting] = useState(false);
  const [activeTab, setActiveTab] = useState("predictions");
  const [daysFilter, setDaysFilter] = useState(14); // 0-14d window (default: next 2 weeks)
  const [watchlist, setWatchlist] = useState(new Set()); // Symbols starred into Sohel Pick
  const [starBusy, setStarBusy] = useState(null); // symbol currently being toggled

  // Apply days filter to bullish/bearish lists
  const filterByDays = (list) => (list || []).filter(p => (p?.days_until ?? 99) <= daysFilter);
  const filteredBullish = filterByDays(predictions?.bullish_predictions);
  const filteredBearish = filterByDays(predictions?.bearish_predictions);

  const fetchWatchlist = useCallback(async () => {
    try {
      const res = await api.get("/sohel-pick/watchlist");
      const syms = new Set((res.data?.watchlist || []).map(w => (w.symbol || "").toUpperCase()));
      setWatchlist(syms);
    } catch {
      // non-critical
    }
  }, []);

  const toggleStar = async (symbol) => {
    if (!symbol) return;
    const sym = symbol.toUpperCase();
    setStarBusy(sym);
    try {
      if (watchlist.has(sym)) {
        await api.delete(`/sohel-pick/watchlist/${sym}`);
        setWatchlist(prev => {
          const next = new Set(prev);
          next.delete(sym);
          return next;
        });
        toast.success(`${sym} removed from Sohel Pick watchlist`);
      } else {
        await api.post(`/sohel-pick/watchlist/add`, { symbol: sym });
        setWatchlist(prev => new Set(prev).add(sym));
        toast.success(`⭐ ${sym} added to Sohel Pick watchlist`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not toggle ${sym}`);
    } finally {
      setStarBusy(null);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [earningsRes, playsRes, predictionsRes] = await Promise.all([
        api.get("/earnings/calendar/upcoming"),
        api.get("/earnings/plays"),
        api.get("/earnings/predictions")
      ]);
      setEarnings(earningsRes.data);
      setEarningsPlays(playsRes.data);
      setPredictions(predictionsRes.data);
    } catch (error) {
      console.error("Error fetching earnings:", error);
      toast.error("Failed to load earnings data");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success("Earnings calendar updated");
  };

  const handlePredict = async (symbol) => {
    const targetSymbol = symbol || searchSymbol;
    if (!targetSymbol.trim()) return;
    
    setPredicting(true);
    setPrediction(null);
    try {
      const response = await api.post(`/earnings/ai-predict/${targetSymbol.toUpperCase()}`);
      setPrediction(response.data);
      setActiveTab("predictor");
      toast.success(`AI prediction ready for ${targetSymbol.toUpperCase()}`);
    } catch (error) {
      console.error("Prediction error:", error);
      toast.error("Failed to generate prediction");
    } finally {
      setPredicting(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchWatchlist();
  }, [fetchData, fetchWatchlist]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "TBD";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch {
      return dateStr;
    }
  };

  const getDaysUntil = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diff = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
      return diff;
    } catch {
      return null;
    }
  };

  const getScoreColor = (score) => {
    if (score >= 70) return "text-green-400";
    if (score >= 55) return "text-emerald-400";
    if (score >= 45) return "text-yellow-400";
    if (score >= 30) return "text-orange-400";
    return "text-red-400";
  };

  const getScoreBg = (score) => {
    if (score >= 70) return "bg-green-500/20 border-green-500/30";
    if (score >= 55) return "bg-emerald-500/20 border-emerald-500/30";
    if (score >= 45) return "bg-yellow-500/20 border-yellow-500/30";
    if (score >= 30) return "bg-orange-500/20 border-orange-500/30";
    return "bg-red-500/20 border-red-500/30";
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="earnings-loading">
        <div className="h-48 skeleton rounded-sm" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-32 skeleton rounded-sm" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="earnings-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-yellow-400" />
            AI EARNINGS PREDICTOR
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Multi-algorithm analysis • Pattern recognition • Price movement predictions
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
          className="gap-2"
          data-testid="refresh-earnings-btn"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* AI Prediction Search */}
      <Card className="bg-gradient-to-r from-purple-500/10 via-blue-500/10 to-cyan-500/10 border-purple-500/20">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="flex-1">
              <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                <Brain className="w-6 h-6 text-purple-400" />
                AI Earnings Prediction Engine
              </h3>
              <p className="text-sm text-muted-foreground">
                Enter any stock ticker to get multi-algorithm earnings prediction with price movement forecasts
              </p>
            </div>
            <div className="flex gap-2 w-full md:w-auto">
              <Input
                placeholder="Enter ticker (e.g., AAPL)"
                value={searchSymbol}
                onChange={(e) => setSearchSymbol(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && handlePredict()}
                className="w-full md:w-48 bg-background"
                data-testid="earnings-search-input"
              />
              <Button 
                onClick={() => handlePredict()}
                disabled={predicting}
                className="bg-purple-600 hover:bg-purple-700 gap-2"
                data-testid="predict-btn"
              >
                {predicting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                Predict
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-card/50">
          <TabsTrigger value="predictions" className="gap-2" data-testid="predictions-tab">
            <Target className="w-4 h-4" />
            🐋 Whale Predictions
          </TabsTrigger>
          <TabsTrigger value="calendar" className="gap-2">
            <Calendar className="w-4 h-4" />
            Calendar
          </TabsTrigger>
          <TabsTrigger value="plays" className="gap-2" data-testid="earnings-plays-tab">
            <Zap className="w-4 h-4" />
            Trade Plays
          </TabsTrigger>
          <TabsTrigger value="predictor" className="gap-2">
            <Brain className="w-4 h-4" />
            AI Predictor
          </TabsTrigger>
        </TabsList>

        {/* NEW: Whale-Powered Predictions Tab */}
        <TabsContent value="predictions" className="space-y-4" data-testid="predictions-content">
          {predictions?.is_loading ? (
            <Card className="card-dark border-cyan-500/30">
              <CardContent className="p-8 text-center">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-cyan-400 mb-4" />
                <p className="text-muted-foreground">Generating whale-powered predictions for 500+ stocks...</p>
                <p className="text-xs text-muted-foreground mt-2">This may take 60-90 seconds on first load</p>
              </CardContent>
            </Card>
          ) : predictions?.predictions?.length > 0 ? (
            <>
              {/* Whale Powered Banner */}
              {predictions.whale_powered && (
                <Card className="border-cyan-500/30 bg-gradient-to-r from-cyan-500/10 to-blue-500/10">
                  <CardContent className="p-4 flex items-center gap-4">
                    <span className="text-4xl">🐋</span>
                    <div>
                      <p className="text-cyan-400 font-bold">Whale-Powered Predictions</p>
                      <p className="text-gray-400 text-sm">
                        Institutional options flow • Sweeps • Smart money positioning
                      </p>
                    </div>
                    {predictions.summary?.success_rate?.success_rate > 0 && (
                      <Badge className="ml-auto bg-green-500/20 text-green-400">
                        {predictions.summary.success_rate.success_rate}% Success Rate
                      </Badge>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card className="card-dark border-emerald-500/30 bg-emerald-500/5">
                  <CardContent className="p-4 text-center">
                    <TrendingUp className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-emerald-400">{predictions.summary?.bullish_count || 0}</p>
                    <p className="text-xs text-muted-foreground">Bullish</p>
                  </CardContent>
                </Card>
                <Card className="card-dark border-red-500/30 bg-red-500/5">
                  <CardContent className="p-4 text-center">
                    <TrendingDown className="w-6 h-6 text-red-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-red-400">{predictions.summary?.bearish_count || 0}</p>
                    <p className="text-xs text-muted-foreground">Bearish</p>
                  </CardContent>
                </Card>
                <Card className="card-dark border-yellow-500/30 bg-yellow-500/5">
                  <CardContent className="p-4 text-center">
                    <Sparkles className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-yellow-400">{predictions.summary?.high_confidence_count || 0}</p>
                    <p className="text-xs text-muted-foreground">High Confidence</p>
                  </CardContent>
                </Card>
                <Card className="card-dark border-blue-500/30 bg-blue-500/5">
                  <CardContent className="p-4 text-center">
                    <BarChart3 className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-blue-400">{predictions.summary?.avg_confidence || 0}%</p>
                    <p className="text-xs text-muted-foreground">Avg Confidence</p>
                  </CardContent>
                </Card>
                <Card className="card-dark border-purple-500/30 bg-purple-500/5">
                  <CardContent className="p-4 text-center">
                    <Target className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-purple-400">{predictions.summary?.total_predictions || 0}</p>
                    <p className="text-xs text-muted-foreground">Total</p>
                  </CardContent>
                </Card>
              </div>

              {/* Days Filter - lets user focus on immediate plays */}
              <Card className="card-dark border-purple-500/20 bg-purple-500/5">
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center gap-3">
                    <div className="flex items-center gap-2 text-sm text-purple-300 font-bold whitespace-nowrap">
                      <Clock className="w-4 h-4" />
                      SHOW PLAYS WITHIN:
                    </div>
                    <div className="flex flex-wrap gap-2" data-testid="days-filter-group">
                      {[3, 5, 7, 14, 21].map((d) => (
                        <button
                          key={d}
                          onClick={() => setDaysFilter(d)}
                          data-testid={`days-filter-${d}`}
                          className={`px-3 py-1.5 rounded-sm text-xs font-bold border transition-colors ${
                            daysFilter === d
                              ? "bg-purple-500/30 border-purple-400 text-purple-100"
                              : "bg-transparent border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                          }`}
                        >
                          {d === 3 ? "NEXT 3 DAYS" : d === 5 ? "THIS WEEK" : `${d} DAYS`}
                        </button>
                      ))}
                    </div>
                    <div className="ml-auto text-xs text-muted-foreground">
                      Showing {filteredBullish.length} bullish · {filteredBearish.length} bearish within {daysFilter}d
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Prediction Cards */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Bullish Predictions */}
                <div className="space-y-3">
                  <h3 className="text-lg font-bold text-emerald-400 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    📈 BULLISH PREDICTIONS
                  </h3>
                  {filteredBullish.slice(0, 20).length > 0 ? filteredBullish.slice(0, 20).map((pred, idx) => (
                    <Card key={idx} className="card-dark border-emerald-500/30 hover:border-emerald-500/50 transition-colors">
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-lg font-bold text-white">{pred.symbol}</span>
                              {pred.scores?.whale > 0 && <span title="Whale Activity">🐋</span>}
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleStar(pred.symbol); }}
                                disabled={starBusy === pred.symbol}
                                data-testid={`star-${pred.symbol}`}
                                title={watchlist.has(pred.symbol) ? "Remove from Sohel Pick" : "Add to Sohel Pick watchlist"}
                                className="ml-auto"
                              >
                                <Star
                                  className={`w-4 h-4 transition-colors ${
                                    watchlist.has(pred.symbol)
                                      ? "fill-yellow-400 text-yellow-400"
                                      : "text-gray-500 hover:text-yellow-400"
                                  } ${starBusy === pred.symbol ? "animate-pulse" : ""}`}
                                />
                              </button>
                            </div>
                            <p className="text-xs text-muted-foreground">{pred.company_name}</p>
                          </div>
                          <Badge className={`${pred.confidence >= 80 ? 'bg-emerald-500/30 text-emerald-300' : 'bg-emerald-500/20 text-emerald-400'}`}>
                            {pred.confidence}% Confidence
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-2 mb-3 text-center">
                          <div className="bg-gray-800/50 rounded p-2">
                            <p className="text-xs text-muted-foreground">Current</p>
                            <p className="text-sm font-bold text-white">${pred.current_price?.toFixed(2)}</p>
                          </div>
                          <div className="bg-emerald-500/10 rounded p-2">
                            <p className="text-xs text-muted-foreground">Target</p>
                            <p className="text-sm font-bold text-emerald-400">${pred.target_price?.toFixed(2)}</p>
                          </div>
                          <div className="bg-emerald-500/10 rounded p-2">
                            <p className="text-xs text-muted-foreground">Move</p>
                            <p className="text-sm font-bold text-emerald-400">+{pred.expected_move_pct}%</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                          <Calendar className="w-3 h-3" />
                          <span>{pred.earnings_date} {pred.earnings_time?.toUpperCase()}</span>
                          {pred.days_until !== undefined && (
                            <Badge variant="outline" className="text-xs">
                              {pred.days_until === 0 ? 'TODAY' : `${pred.days_until}d`}
                            </Badge>
                          )}
                        </div>
                        
                        {/* Suggested trade from backend (proper Friday expiry) */}
                        {(pred.suggested_trade || pred.direction === 'UP') && (
                          <div className="p-2 bg-emerald-500/10 border border-emerald-500/30 rounded mb-2">
                            <p className="text-xs text-emerald-400 uppercase font-bold mb-1">Suggested Trade</p>
                            <p className="text-xs font-data font-bold text-emerald-300">
                              {pred.suggested_trade || `BUY ${pred.symbol} CALL (loading...)`}
                            </p>
                          </div>
                        )}
                        
                        {/* Whale flow details */}
                        {pred.whale_analysis?.top_trade && (
                          <div className="p-2 bg-yellow-500/10 border border-yellow-500/20 rounded mb-2">
                            <p className="text-xs text-yellow-400 font-bold">Whale Trade</p>
                            <p className="text-xs font-data text-yellow-300">{pred.whale_analysis.top_trade}</p>
                          </div>
                        )}
                        
                        {pred.key_factors?.length > 0 && (
                          <div className="space-y-1 mt-2 pt-2 border-t border-gray-700/50">
                            {pred.key_factors.slice(0, 4).map((factor, i) => (
                              <p key={i} className="text-xs text-gray-400">{factor}</p>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )) : (
                    <Card className="card-dark border-gray-700">
                      <CardContent className="p-8 text-center">
                        <TrendingUp className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                        <p className="text-muted-foreground">No bullish plays within {daysFilter} days</p>
                        <p className="text-xs text-gray-600 mt-1">Try expanding the day range above</p>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Bearish Predictions */}
                <div className="space-y-3">
                  <h3 className="text-lg font-bold text-red-400 flex items-center gap-2">
                    <TrendingDown className="w-5 h-5" />
                    📉 BEARISH PREDICTIONS
                  </h3>
                  {predictions.bearish_predictions?.length > 0 ? (
                    filteredBearish.length > 0 ? filteredBearish.slice(0, 20).map((pred, idx) => (
                      <Card key={idx} className="card-dark border-red-500/30 hover:border-red-500/50 transition-colors">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-lg font-bold text-white">{pred.symbol}</span>
                                {pred.scores?.whale < 0 && <span title="Whale Activity">🐋</span>}
                                <button
                                  onClick={(e) => { e.stopPropagation(); toggleStar(pred.symbol); }}
                                  disabled={starBusy === pred.symbol}
                                  data-testid={`star-${pred.symbol}`}
                                  title={watchlist.has(pred.symbol) ? "Remove from Sohel Pick" : "Add to Sohel Pick watchlist"}
                                  className="ml-auto"
                                >
                                  <Star
                                    className={`w-4 h-4 transition-colors ${
                                      watchlist.has(pred.symbol)
                                        ? "fill-yellow-400 text-yellow-400"
                                        : "text-gray-500 hover:text-yellow-400"
                                    } ${starBusy === pred.symbol ? "animate-pulse" : ""}`}
                                  />
                                </button>
                              </div>
                              <p className="text-xs text-muted-foreground">{pred.company_name}</p>
                            </div>
                            <Badge className={`${pred.confidence >= 80 ? 'bg-red-500/30 text-red-300' : 'bg-red-500/20 text-red-400'}`}>
                              {pred.confidence}% Confidence
                            </Badge>
                          </div>
                          
                          <div className="grid grid-cols-3 gap-2 mb-3 text-center">
                            <div className="bg-gray-800/50 rounded p-2">
                              <p className="text-xs text-muted-foreground">Current</p>
                              <p className="text-sm font-bold text-white">${pred.current_price?.toFixed(2)}</p>
                            </div>
                            <div className="bg-red-500/10 rounded p-2">
                              <p className="text-xs text-muted-foreground">Target</p>
                              <p className="text-sm font-bold text-red-400">${pred.target_price?.toFixed(2)}</p>
                            </div>
                            <div className="bg-red-500/10 rounded p-2">
                              <p className="text-xs text-muted-foreground">Move</p>
                              <p className="text-sm font-bold text-red-400">-{pred.expected_move_pct}%</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                            <Calendar className="w-3 h-3" />
                            <span>{pred.earnings_date} {pred.earnings_time?.toUpperCase()}</span>
                            {pred.days_until !== undefined && (
                              <Badge variant="outline" className="text-xs">
                                {pred.days_until === 0 ? 'TODAY' : `${pred.days_until}d`}
                              </Badge>
                            )}
                          </div>

                          {/* Suggested trade from backend */}
                          {(pred.suggested_trade || pred.direction === 'DOWN') && (
                            <div className="p-2 bg-red-500/10 border border-red-500/30 rounded mb-2">
                              <p className="text-xs text-red-400 uppercase font-bold mb-1">Suggested Trade</p>
                              <p className="text-xs font-data font-bold text-red-300">
                                {pred.suggested_trade || `BUY ${pred.symbol} PUT (loading...)`}
                              </p>
                            </div>
                          )}

                          {pred.whale_analysis?.top_trade && (
                            <div className="p-2 bg-yellow-500/10 border border-yellow-500/20 rounded mb-2">
                              <p className="text-xs text-yellow-400 font-bold">Whale Trade</p>
                              <p className="text-xs font-data text-yellow-300">{pred.whale_analysis.top_trade}</p>
                            </div>
                          )}
                          
                          {pred.key_factors?.length > 0 && (
                            <div className="space-y-1 mt-2 pt-2 border-t border-gray-700/50">
                              {pred.key_factors.slice(0, 4).map((factor, i) => (
                                <p key={i} className="text-xs text-gray-400">{factor}</p>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )) : (
                      <Card className="card-dark border-gray-700">
                        <CardContent className="p-8 text-center">
                          <TrendingDown className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                          <p className="text-muted-foreground">No bearish plays within {daysFilter} days</p>
                          <p className="text-xs text-gray-600 mt-1">Try expanding the day range above</p>
                        </CardContent>
                      </Card>
                    )
                  ) : (
                    <Card className="card-dark border-gray-700">
                      <CardContent className="p-8 text-center">
                        <TrendingDown className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                        <p className="text-muted-foreground">No bearish predictions at this time</p>
                        <p className="text-xs text-gray-600 mt-1">Market sentiment is bullish</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>

              {/* Data Sources */}
              <div className="flex justify-center gap-2 flex-wrap">
                {predictions.data_sources?.map((source, i) => (
                  <Badge key={i} variant="outline" className="text-xs text-gray-500">
                    {source}
                  </Badge>
                ))}
              </div>
            </>
          ) : (
            <Card className="card-dark border-gray-700">
              <CardContent className="p-8 text-center">
                <Calendar className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                <p className="text-muted-foreground">No predictions available</p>
                <p className="text-xs text-gray-600 mt-2">Click Refresh to generate predictions</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Earnings Plays Tab */}
        <TabsContent value="plays" className="space-y-4" data-testid="earnings-plays-content">
          {earningsPlays?.is_loading ? (
            <Card className="card-dark border-blue-500/30">
              <CardContent className="p-8 text-center">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-blue-400 mb-4" />
                <p className="text-muted-foreground">Analyzing earnings + options flow...</p>
                <p className="text-xs text-muted-foreground mt-2">This may take 30 seconds</p>
              </CardContent>
            </Card>
          ) : earningsPlays?.plays?.length > 0 ? (
            <>
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="card-dark border-green-500/30 bg-green-500/5">
                  <CardContent className="p-4 text-center">
                    <ArrowUpRight className="w-6 h-6 text-green-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-green-400">{earningsPlays.bullish_plays?.length || 0}</p>
                    <p className="text-xs text-muted-foreground">Bullish Plays</p>
                  </CardContent>
                </Card>
                <Card className="card-dark border-red-500/30 bg-red-500/5">
                  <CardContent className="p-4 text-center">
                    <ArrowDownRight className="w-6 h-6 text-red-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-red-400">{earningsPlays.bearish_plays?.length || 0}</p>
                    <p className="text-xs text-muted-foreground">Bearish Plays</p>
                  </CardContent>
                </Card>
                <Card className="card-dark border-purple-500/30 bg-purple-500/5">
                  <CardContent className="p-4 text-center">
                    <Activity className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-purple-400">{earningsPlays.strangle_plays?.length || 0}</p>
                    <p className="text-xs text-muted-foreground">Strangles</p>
                  </CardContent>
                </Card>
                <Card className="card-dark border-yellow-500/30 bg-yellow-500/5">
                  <CardContent className="p-4 text-center">
                    <Target className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-yellow-400">{earningsPlays.total_opportunities || 0}</p>
                    <p className="text-xs text-muted-foreground">Total Plays</p>
                  </CardContent>
                </Card>
              </div>

              {/* Play Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {earningsPlays.plays.map((play, idx) => {
                  const playTypeColors = {
                    "BUY_CALLS": "border-green-500/50 bg-green-500/10",
                    "WEAK_CALLS": "border-green-500/30 bg-green-500/5",
                    "BUY_PUTS": "border-red-500/50 bg-red-500/10",
                    "WEAK_PUTS": "border-red-500/30 bg-red-500/5",
                    "STRANGLE": "border-purple-500/50 bg-purple-500/10",
                    "AVOID": "border-gray-500/30 bg-gray-500/5"
                  };
                  const playTypeBadge = {
                    "BUY_CALLS": "bg-green-500/20 text-green-400",
                    "WEAK_CALLS": "bg-green-500/10 text-green-300",
                    "BUY_PUTS": "bg-red-500/20 text-red-400",
                    "WEAK_PUTS": "bg-red-500/10 text-red-300",
                    "STRANGLE": "bg-purple-500/20 text-purple-400",
                    "AVOID": "bg-gray-500/20 text-gray-400"
                  };
                  const playTypeIcon = {
                    "BUY_CALLS": <ArrowUpRight className="w-4 h-4" />,
                    "WEAK_CALLS": <TrendingUp className="w-4 h-4" />,
                    "BUY_PUTS": <ArrowDownRight className="w-4 h-4" />,
                    "WEAK_PUTS": <TrendingDown className="w-4 h-4" />,
                    "STRANGLE": <Activity className="w-4 h-4" />,
                    "AVOID": <ShieldAlert className="w-4 h-4" />
                  };

                  return (
                    <Card 
                      key={idx}
                      className={`card-dark ${playTypeColors[play.play_type] || 'border-gray-500/30'} hover:scale-[1.02] transition-transform cursor-pointer`}
                      data-testid={`earnings-play-${play.symbol}`}
                      onClick={() => handlePredict(play.symbol)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-heading font-bold text-xl">{play.symbol}</p>
                            <p className="text-xs text-muted-foreground">{play.company_name || play.name}</p>
                          </div>
                          <Badge className={playTypeBadge[play.play_type] || 'bg-gray-500/20'}>
                            <span className="flex items-center gap-1">
                              {playTypeIcon[play.play_type]}
                              {play.play_type?.replace('_', ' ')}
                            </span>
                          </Badge>
                        </div>

                        {/* THE TRADE - what to actually buy */}
                        {play.contract && (
                          <div className="p-3 bg-ai/10 border border-ai/30 rounded-lg mb-3">
                            <p className="text-xs text-ai uppercase tracking-wider mb-1">Copy This Trade</p>
                            <p className="font-data font-bold text-sm text-ai">{play.contract}</p>
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                          <div className="p-2 bg-black/20 rounded">
                            <p className="text-xs text-muted-foreground">Price</p>
                            <p className="font-mono font-bold">${play.current_price?.toFixed(2)}</p>
                          </div>
                          <div className="p-2 bg-black/20 rounded">
                            <p className="text-xs text-muted-foreground">Strike</p>
                            <p className="font-mono font-bold">${play.strike}</p>
                          </div>
                          <div className="p-2 bg-black/20 rounded">
                            <p className="text-xs text-muted-foreground">Expiry</p>
                            <p className="font-mono">{play.expiry_display || play.expiry || 'TBD'}</p>
                          </div>
                          <div className="p-2 bg-black/20 rounded">
                            <p className="text-xs text-muted-foreground">Earnings In</p>
                            <p className="font-mono font-bold">{play.days_until_earnings}d</p>
                          </div>
                        </div>

                        {/* Whale trade */}
                        {play.whale_trade && (
                          <div className="p-2 bg-yellow-500/10 border border-yellow-500/20 rounded mb-3">
                            <p className="text-xs text-yellow-400 uppercase mb-1">Whale Flow</p>
                            <p className="text-xs font-data text-yellow-300">{play.whale_trade}</p>
                          </div>
                        )}

                        {/* Polygon pattern */}
                        {play.polygon_pattern && (
                          <div className="p-2 bg-blue-500/10 border border-blue-500/20 rounded mb-3">
                            <p className="text-xs text-blue-400 uppercase mb-1">Chart Pattern</p>
                            <p className="text-xs font-data text-blue-300">{play.polygon_pattern}</p>
                          </div>
                        )}

                        <div className="flex items-center justify-between text-sm mb-2">
                          <span className="text-muted-foreground">Confidence</span>
                          <span className={`font-bold ${play.confidence >= 70 ? 'text-green-400' : play.confidence >= 60 ? 'text-yellow-400' : 'text-gray-400'}`}>
                            {play.confidence}%
                          </span>
                        </div>

                        {play.reasoning?.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-white/10">
                            {play.reasoning.map((r, i) => (
                              <p key={i} className="text-xs text-muted-foreground">{r}</p>
                            ))}
                          </div>
                        )}

                        <div className="mt-3 flex items-center justify-between">
                          <Badge variant="outline" className={play.risk_level === 'HIGH' ? 'border-red-500/50 text-red-400' : 'border-yellow-500/50 text-yellow-400'}>
                            {play.risk_level} RISK
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            Target: ${play.targets?.bullish || '?'} / ${play.targets?.bearish || '?'}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </>
          ) : (
            <Card className="card-dark">
              <CardContent className="p-8 text-center">
                <Zap className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No earnings plays available</p>
                <p className="text-xs text-muted-foreground mt-2">Check back when earnings season is active</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Calendar Tab */}
        <TabsContent value="calendar" className="space-y-4">
          {/* Portfolio Earnings Alert */}
          {earnings?.portfolio_earnings?.length > 0 && (
            <Card className="card-dark border-warning/50 bg-warning/5" data-testid="portfolio-earnings-alert">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading flex items-center gap-2 text-warning">
                  <AlertTriangle className="w-5 h-5" />
                  YOUR HOLDINGS - UPCOMING EARNINGS
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {earnings.portfolio_earnings.slice(0, 6).map((stock, idx) => {
                    const daysUntil = getDaysUntil(stock.earnings_date);
                    return (
                      <Card 
                        key={idx} 
                        className="card-dark animate-slide-in cursor-pointer hover:border-purple-500/50 transition-colors"
                        onClick={() => handlePredict(stock.symbol)}
                        data-testid={`portfolio-earning-${stock.symbol}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <p className="font-heading font-bold text-xl">{stock.symbol}</p>
                              <p className="text-xs text-muted-foreground line-clamp-1">{stock.name}</p>
                            </div>
                            <Badge className="bg-warning/20 text-warning">
                              {daysUntil !== null && daysUntil <= 7 ? `${daysUntil}d` : formatDate(stock.earnings_date)}
                            </Badge>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">Last Surprise</span>
                            <span className={`font-bold ${stock.last_surprise >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {stock.last_surprise >= 0 ? '+' : ''}{stock.last_surprise?.toFixed(1)}%
                            </span>
                          </div>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="w-full mt-2 gap-2 text-purple-400 hover:text-purple-300"
                          >
                            <Brain className="w-4 h-4" />
                            AI Predict
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Full Calendar */}
          <Card className="card-dark">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                TOP STOCKS EARNINGS ({earnings?.total_tracked || 0} tracked)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <div className="space-y-2">
                  {earnings?.upcoming_earnings?.map((stock, idx) => {
                    const daysUntil = getDaysUntil(stock.earnings_date);
                    return (
                      <div
                        key={idx}
                        className={`flex items-center justify-between p-4 bg-background border border-border rounded-lg hover:border-purple-500/50 cursor-pointer transition-colors ${stock.in_portfolio ? 'border-l-4 border-l-warning' : ''}`}
                        onClick={() => handlePredict(stock.symbol)}
                        data-testid={`earning-${stock.symbol}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 flex flex-col items-center justify-center rounded ${
                            daysUntil !== null && daysUntil <= 3 ? 'bg-red-500/20' :
                            daysUntil !== null && daysUntil <= 7 ? 'bg-yellow-500/20' :
                            'bg-muted'
                          }`}>
                            <Calendar className={`w-5 h-5 ${
                              daysUntil !== null && daysUntil <= 3 ? 'text-red-400' :
                              daysUntil !== null && daysUntil <= 7 ? 'text-yellow-400' :
                              'text-muted-foreground'
                            }`} />
                            {daysUntil !== null && daysUntil <= 14 && (
                              <span className="text-xs font-bold">{daysUntil}d</span>
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-heading font-bold text-lg">{stock.symbol}</p>
                              {stock.in_portfolio && (
                                <Badge variant="outline" className="text-xs text-warning border-warning/50">
                                  HOLDING
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{stock.name}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-6">
                          <div className="text-center hidden md:block">
                            <p className="text-xs text-muted-foreground">Price</p>
                            <p className="font-data font-bold">${stock.current_price?.toFixed(2)}</p>
                          </div>
                          <div className="text-center hidden md:block">
                            <p className="text-xs text-muted-foreground">Last Surprise</p>
                            <p className={`font-data font-bold ${stock.last_surprise >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {stock.last_surprise >= 0 ? '+' : ''}{stock.last_surprise?.toFixed(1)}%
                            </p>
                          </div>
                          <div className="text-right min-w-[100px]">
                            <p className="font-data font-bold">{formatDate(stock.earnings_date)}</p>
                            <Button size="sm" variant="ghost" className="h-6 px-2 text-purple-400">
                              <Brain className="w-3 h-3 mr-1" />
                              Predict
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Predictor Tab */}
        <TabsContent value="predictor" className="space-y-4">
          {prediction ? (
            <div className="space-y-4">
              {/* Prediction Header */}
              <Card className={`border-2 ${getScoreBg(prediction.prediction?.composite_score)}`}>
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex items-center gap-4">
                      <div className={`w-20 h-20 rounded-xl flex items-center justify-center text-4xl ${getScoreBg(prediction.prediction?.composite_score)}`}>
                        {prediction.prediction?.emoji}
                      </div>
                      <div>
                        <h2 className="text-3xl font-bold text-white">{prediction.symbol}</h2>
                        <p className="text-muted-foreground">{prediction.name}</p>
                        <p className="text-2xl font-bold mt-1">${prediction.current_price}</p>
                      </div>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground mb-1">AI PREDICTION</p>
                      <p className={`text-4xl font-bold ${getScoreColor(prediction.prediction?.composite_score)}`}>
                        {prediction.prediction?.verdict}
                      </p>
                      <Badge className={`mt-2 ${getScoreBg(prediction.prediction?.composite_score)}`}>
                        {prediction.prediction?.confidence} CONFIDENCE
                      </Badge>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground mb-1">COMPOSITE SCORE</p>
                      <p className={`text-6xl font-bold ${getScoreColor(prediction.prediction?.composite_score)}`}>
                        {prediction.prediction?.composite_score?.toFixed(0)}
                      </p>
                      <p className="text-sm text-muted-foreground">/100</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Algorithm Scores */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {prediction.algorithms && Object.entries(prediction.algorithms).map(([key, algo]) => (
                  <Card key={key} className="bg-card/50">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <BarChart3 className="w-4 h-4 text-purple-400" />
                        <p className="text-xs text-muted-foreground uppercase">{key.replace(/_/g, ' ')}</p>
                      </div>
                      <p className={`text-3xl font-bold ${getScoreColor(algo.score)}`}>
                        {algo.score?.toFixed(0)}
                      </p>
                      <Progress value={algo.score} className="h-2 mt-2" />
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Expected Move */}
              <Card className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border-cyan-500/20">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-cyan-400" />
                    EXPECTED PRICE MOVEMENT
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-6 text-center">
                    <div>
                      <p className="text-sm text-muted-foreground">Direction</p>
                      <p className={`text-2xl font-bold ${prediction.expected_move?.direction === 'UP' ? 'text-green-400' : 'text-red-400'}`}>
                        {prediction.expected_move?.direction === 'UP' ? '📈' : '📉'} {prediction.expected_move?.direction}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Expected Move</p>
                      <p className="text-2xl font-bold text-white">
                        ±{prediction.expected_move?.percentage}%
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Price Range</p>
                      <p className="text-lg font-bold">
                        <span className="text-red-400">${prediction.expected_move?.price_low}</span>
                        {' - '}
                        <span className="text-green-400">${prediction.expected_move?.price_high}</span>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Historical Performance */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="bg-card/50">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <BarChart3 className="w-5 h-5" />
                      Earnings History
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-400" />
                        <span className="text-green-400 font-bold">{prediction.stats?.beat_count} Beats</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <XCircle className="w-5 h-5 text-red-400" />
                        <span className="text-red-400 font-bold">{prediction.stats?.miss_count} Misses</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {prediction.earnings_history?.map((eh, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-background rounded">
                          <span className="text-sm text-muted-foreground">{eh.date}</span>
                          <span className={`font-bold ${eh.surprise_pct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {eh.surprise_pct >= 0 ? '+' : ''}{eh.surprise_pct?.toFixed(1)}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-card/50">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" />
                      Post-Earnings Price Moves
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {prediction.price_movements?.map((pm, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-background rounded">
                          <div>
                            <span className="text-sm text-muted-foreground">{pm.date}</span>
                            <span className={`ml-2 text-xs ${pm.surprise >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              ({pm.surprise >= 0 ? '+' : ''}{pm.surprise?.toFixed(1)}% surprise)
                            </span>
                          </div>
                          <span className={`font-bold ${pm.price_move >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {pm.price_move >= 0 ? '+' : ''}{pm.price_move?.toFixed(1)}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* AI Analysis */}
              {prediction.ai_analysis && (
                <Card className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-purple-500/20">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Brain className="w-5 h-5 text-purple-400" />
                      AI DEEP ANALYSIS
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-white whitespace-pre-wrap">{prediction.ai_analysis}</p>
                  </CardContent>
                </Card>
              )}

              {/* Technicals */}
              <Card className="bg-card/50">
                <CardHeader>
                  <CardTitle className="text-lg">Technical Indicators</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-3 bg-background rounded">
                      <p className="text-xs text-muted-foreground">RSI</p>
                      <p className={`text-xl font-bold ${
                        prediction.technicals?.rsi < 30 ? 'text-green-400' :
                        prediction.technicals?.rsi > 70 ? 'text-red-400' : 'text-white'
                      }`}>
                        {prediction.technicals?.rsi}
                      </p>
                    </div>
                    <div className="p-3 bg-background rounded">
                      <p className="text-xs text-muted-foreground">Trend</p>
                      <p className={`text-xl font-bold ${
                        prediction.technicals?.trend === 'uptrend' ? 'text-green-400' :
                        prediction.technicals?.trend === 'downtrend' ? 'text-red-400' : 'text-yellow-400'
                      }`}>
                        {prediction.technicals?.trend?.toUpperCase()}
                      </p>
                    </div>
                    <div className="p-3 bg-background rounded">
                      <p className="text-xs text-muted-foreground">MACD</p>
                      <p className={`text-xl font-bold ${
                        prediction.technicals?.macd === 'bullish' ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {prediction.technicals?.macd?.toUpperCase()}
                      </p>
                    </div>
                    <div className="p-3 bg-background rounded">
                      <p className="text-xs text-muted-foreground">Volatility</p>
                      <p className="text-xl font-bold text-white">
                        {prediction.stats?.volatility}%
                      </p>
                    </div>
                  </div>
                  {prediction.technicals?.patterns?.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {prediction.technicals.patterns.map((p, idx) => (
                        <Badge key={idx} variant="outline">{p}</Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card className="bg-card/50">
              <CardContent className="p-12 text-center">
                <Brain className="w-16 h-16 text-purple-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">AI Earnings Predictor</h3>
                <p className="text-muted-foreground mb-4">
                  Enter a stock ticker above or click "Predict" on any stock in the calendar
                </p>
                <p className="text-sm text-muted-foreground">
                  Our multi-algorithm engine analyzes historical patterns, technicals, and price movements
                  to predict earnings outcomes and expected stock price changes.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
